import React from 'react';
import { ArrowRightIcon, CheckIcon } from 'lucide-react';
import { valueStack, valuePriced, valueTotals, offers, media } from '../data/product';
import { CtaButton } from './CtaButton';
import { requestOffer } from '../utils/offerBus';
import { useTrackOnView } from '../hooks/useTrackOnView';
import { GuaranteeBadge, ShopHotspot } from './BannerOverlays';
export function ValueStackSection() {
  const ref = useTrackOnView<HTMLElement>('value_stack_viewed');
  return <section ref={ref} aria-labelledby="value-heading" className="border-b border-white/10 bg-ink-950" data-id="element-1583" data-mp="tph0wkf6vpx4">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-1584" data-mp="12oc8o0xvt82j">
        <h2 id="value-heading" className="grit-headline max-w-3xl text-[clamp(2.25rem,8vw,4rem)] font-extrabold uppercase text-offwhite" data-id="element-1585" data-mp="12m7vsti08yq4">
          The complete
          <br data-id="element-1586" data-mp="2e1o8m0qit6jf" />
          <span className="text-grit-500" data-id="element-1587" data-mp="2e5c0ywk8bmwk">GritVue X1 system.</span>
        </h2>
        <p className="mt-4 max-w-2xl text-lg text-steel-300" data-id="element-1588" data-mp="2a1mgai6odpgt">
          Built for real work. The GritVue X1™ combines hands-free illumination, interchangeable lenses and a
          complete care system in one premium, work-ready package.
        </p>

        <div className="mt-10 grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start" data-id="element-1589" data-mp="1wd510sx3jg9f">
          <div className="border border-white/12 bg-ink-900" data-id="element-1590" data-mp="21chsxs2trdiv">
            <p className="border-b border-white/10 px-5 py-4 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400 sm:px-6" data-id="element-1591" data-mp="21ane8vi5chn8">
              Everything included
            </p>
            <ul data-id="element-1592" data-mp="18oly6swtfvoq">
              {valuePriced.map(row => <li key={row.item} className="flex items-start justify-between gap-5 border-b border-white/5 px-5 py-4 sm:px-6" data-id="element-1593" data-mp="1dmx4itnwjo27">
                  <span className="min-w-0" data-id="element-1594" data-mp="kyrhg09dlmz7">
                    <span className="block text-base font-semibold text-offwhite sm:text-lg" data-id="element-1595" data-mp="kxyl7t81mfvb">{row.item}</span>
                    <span className="mt-0.5 block text-[13px] text-steel-500" data-id="element-1596" data-mp="pvrrsnd9dbw4">{row.note}</span>
                  </span>
                  <span className="shrink-0 text-right" data-id="element-1597" data-mp="1t65l55tf8pdu">
                    <span className="block font-display text-2xl font-bold leading-none text-steel-300" data-id="element-1598" data-mp="1o8m5c1h2s4js">
                      {row.price}
                    </span>
                    {row.price.startsWith('$') && <span className="mt-0.5 block text-[10px] font-bold uppercase tracking-[0.16em] text-steel-500" data-id="element-1599" data-mp="1y00b2qzme0ld">
                        Value
                      </span>}
                  </span>
                </li>)}
            </ul>

            <div className="flex items-center justify-between gap-5 border-b border-white/10 px-5 py-4 sm:px-6" data-id="element-1600" data-mp="1jb3v87rbnja7">
              <span className="font-display text-xl font-bold uppercase tracking-wide text-offwhite" data-id="element-1601" data-mp="1e9obcj0vrite">
                Total separate value
              </span>
              <span className="font-display text-3xl font-extrabold leading-none text-offwhite" data-id="element-1602" data-mp="2pqwel0zsisvz">
                {valueTotals.total}
              </span>
            </div>
            <div className="flex items-center justify-between gap-5 border-b border-white/10 px-5 py-4 sm:px-6" data-id="element-1603" data-mp="2y9rjna5qbgik">
              <span className="min-w-0" data-id="element-1604" data-mp="2o9h9xziwkr85">
                <span className="block font-display text-xl font-bold uppercase tracking-wide text-steel-400" data-id="element-1605" data-mp="2oaitythwco20">
                  Built piece by piece
                </span>
                <span className="mt-0.5 block text-[13px] text-steel-500" data-id="element-1606" data-mp="3gxmtiiy80a6e">X1 Essential + Full Lens System + hard case</span>
              </span>
              <span className="font-display text-3xl font-extrabold leading-none text-steel-400" data-id="element-1607" data-mp="3lw6f2i8wjjy8">
                {valueTotals.builtSeparately}
              </span>
            </div>
            <div className="flex items-center justify-between gap-5 border-b border-white/10 bg-ink-850 px-5 py-4 sm:px-6" data-id="element-1608" data-mp="3c5vaqulb38qt">
              <span className="font-display text-xl font-bold uppercase tracking-wide text-offwhite" data-id="element-1609" data-mp="3pa1rdsocfnp">
                As a Complete System
              </span>
              <span className="text-right" data-id="element-1610" data-mp="2g2pidfdubj0z">
                <span className="block font-display text-3xl font-extrabold leading-none text-grit-500" data-id="element-1611" data-mp="2fzbhycoqljw6">
                  {valueTotals.payOne}
                </span>
                <span className="mt-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-grit-500" data-id="element-1612" data-mp="271hza4jwaorh">
                  {valueTotals.saveVsBuilt} less, plus pouch and cloth
                </span>
              </span>
            </div>

            <a href="#offer" onClick={event => {
            event.preventDefault();
            requestOffer('single');
          }} className="group block bg-grit-500 px-5 py-6 text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite sm:px-6" data-id="element-1613" data-mp="2uqkdt3j8g1hr">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em]" data-id="element-1614" data-mp="3dodes0aaq1ci">Complete System price</p>
              <p className="mt-2 flex flex-wrap items-end gap-x-4 gap-y-1" data-id="element-1615" data-mp="3l1uu8meo2wrb">
                <span className="font-display text-5xl font-extrabold leading-none sm:text-6xl" data-id="element-1616" data-mp="3pyup0162gd34">
                  {valueTotals.payOne}
                </span>
                <span className="pb-1.5 font-display text-lg font-bold uppercase sm:text-xl" data-id="element-1617" data-mp="3v13lh7i5dx9z">
                  for the complete system
                </span>
              </p>
              <p className="mt-3 font-display text-xl font-extrabold uppercase leading-tight sm:text-2xl" data-id="element-1618" data-mp="7thyz79wwk3v">
                Or {valueTotals.payEach} per X1 with Buy 2, Get 1 Free.
              </p>
              <p className="mt-4 inline-flex items-center gap-2 font-display text-xl font-extrabold uppercase tracking-wide" data-id="element-1619" data-mp="hkcmsg2t3rrf">
                Choose your pack
                <ArrowRightIcon className="h-5 w-5 transition-transform duration-150 ease-grit group-hover:translate-x-1" aria-hidden="true" data-id="element-1620" data-mp="rik08oxvzroo" />
              </p>
            </a>
          </div>

          <div data-id="element-1621" data-mp="2skhvf5aheklo">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1622" data-mp="2si5d2r0ta883">In every Complete System</p>
            <ul className="mt-4 max-w-md" data-id="element-1623" data-mp="31cjw94ogq2wh">
              {valueStack.map(item => <li key={item} className="flex items-center gap-3 border-b border-white/10 py-3.5 text-base font-medium text-offwhite sm:text-lg" data-id="element-1624" data-mp="31eeicmbh0125">
                  <CheckIcon className="h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1625" data-mp="3bckdml2ru8y1" />
                  {item}
                </li>)}
            </ul>

            <figure className="mt-8 border border-white/10 bg-ink-900" data-id="element-1626" data-mp="3kamortg7yhz1">
              <div className="relative" data-id="element-1627" data-mp="3gavs5940h7ml">
                <img src={media.detailSheet} alt="The GritVue X1 frame with its light module, clear, amber and smoke lenses and USB-C charging" className="block h-auto w-full" data-id="element-1628" data-mp="3g7q3fuzao7re" />
                <GuaranteeBadge compact box={{
                left: '17.2%',
                top: '37.4%',
                width: '17.4%',
                height: '9.2%'
              }} data-id="element-1629" data-mp="1brmhgixh33rw" />
                <ShopHotspot box={{
                left: '3.3%',
                top: '38.4%',
                width: '14.2%',
                height: '7%'
              }} data-id="element-1630" data-mp="1222y7c5ul7wi" />
              </div>
              <figcaption className="border-t border-white/10 px-4 py-3 text-[13px] text-steel-400" data-id="element-1631" data-mp="170mkxes9mnpx">
                The X1 Complete System: illuminated frame, all three lenses and USB-C charging.
              </figcaption>
            </figure>

            <ul className="mt-6 grid grid-cols-1 gap-px bg-white/10 sm:grid-cols-3" data-id="element-1632" data-mp="x4jjkb58mt41">
              {offers.map(offer => <li key={offer.id} data-id="element-1633" data-mp="j5jx9a576qb1">
                  <a href="#offer" onClick={event => {
                event.preventDefault();
                requestOffer(offer.id);
              }} aria-label={`${offer.units} for ${offer.price}. Go to this pack`} className={`block h-full px-4 py-4 transition-[background-color,transform] duration-150 ease-grit active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${offer.featured ? 'bg-grit-500 text-ink-950 hover:bg-grit-400' : 'bg-ink-900 hover:bg-ink-800'}`} data-id="element-1634" data-mp="o1iemds3z81y">
                    <p className={`font-display text-xl font-extrabold uppercase leading-none ${offer.featured ? 'text-ink-950' : 'text-offwhite'}`} data-id="element-1635" data-mp="38a95okygw04t">
                      {offer.kicker}
                    </p>
                    <p className={`mt-1 font-display text-3xl font-extrabold leading-none ${offer.featured ? 'text-ink-950' : 'text-grit-500'}`} data-id="element-1636" data-mp="2uahlg2hcxnid">
                      {offer.price}
                    </p>
                    <p className={`mt-1 text-[11px] font-bold uppercase tracking-wide ${offer.featured ? 'text-ink-950/70' : 'text-steel-400'}`} data-id="element-1637" data-mp="2agrarlhng9mr">
                      {offer.perUnit ?? offer.discountBadge ?? 'Single system'}
                    </p>
                  </a>
                </li>)}
            </ul>

            <p className="grit-headline mt-8 text-[clamp(1.75rem,6vw,3rem)] font-extrabold uppercase text-offwhite" data-id="element-1638" data-mp="1rif200unh1rp">
              See further.
              <br data-id="element-1639" data-mp="1whi5hguzezxk" />
              <span className="text-grit-500" data-id="element-1640" data-mp="1wj2tsoqpdmmz">Work smarter.</span>
            </p>
            <p className="mt-3 text-[15px] font-semibold uppercase tracking-wide text-steel-300" data-id="element-1641" data-mp="20h8txdx1d3yx">
              Built for the people who keep America moving.
            </p>
            <p className="mt-1 text-[13px] text-steel-500" data-id="element-1642" data-mp="3bwp5khttf5qn">Prescription ready.</p>

            <CtaButton offer="trio" className="mt-6 w-full sm:w-auto" data-id="element-1643" data-mp="3gwabxdbbegde">
              Get 3 X1 Systems for $99.90
            </CtaButton>
          </div>
        </div>
      </div>
    </section>;
}